/** Staff / shift / commission data hooks (ST-01/02/03/04). PostgREST reads +
 *  CRUD via @gym/api-client; clock-in/out + accrual go through the staff-ops
 *  Edge Function. */
import {
  clockIn,
  clockOut,
  createCommissionRule,
  createStaff,
  deactivateCommissionRule,
  deactivateStaff,
  listCommissionRules,
  listCommissionSummary,
  listShifts,
  listStaff,
  updateStaff,
  type CreateCommissionRuleInput,
  type CreateStaffInput,
  type UpdateStaffInput,
} from '@gym/api-client';
import type { schemas } from '@gym/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../../lib/auth.js';
import { supabase } from '../../../lib/supabase.js';

export interface StaffRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  app_role: string;
  locale: string | null;
  is_active: boolean;
  hired_at: string | null;
  // Payroll + HR profile (migration 0143). Salaries are minor units (MKD*100).
  gross_salary: number | null;
  net_salary: number | null;
  iban: string | null;
  national_id: string | null;
  address: string | null;
  date_of_birth: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  employment_type: string | null;
  notes: string | null;
}

export function useStaffList(opts: { isActive?: boolean; appRole?: StaffRow['app_role'] } = {}) {
  return useQuery({
    queryKey: ['staff', opts],
    queryFn: async (): Promise<StaffRow[]> => {
      const { data, error } = await listStaff(supabase, opts as never);
      if (error) throw error;
      return (data ?? []) as unknown as StaffRow[];
    },
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  const { claims } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateStaffInput) => {
      const { data, error } = await createStaff(supabase, claims?.tenant_id ?? '', input);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; input: UpdateStaffInput }) => {
      const { data, error } = await updateStaff(supabase, vars.id, vars.input);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
  });
}

export function useDeactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await deactivateStaff(supabase, id);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
  });
}

/** Raised when an employee has no бруто salary set; the UI toasts staff.noSalarySet. */
export class NoSalarySetError extends Error {
  constructor() {
    super('no_salary_set');
    this.name = 'NoSalarySetError';
  }
}

/**
 * Record an employee's monthly бруто (gross) salary as a `salary` expense so it
 * flows into Analytics → Expense overview / P&L. Inserts directly via PostgREST
 * (matches the AddExpenseModal pattern; owner/manager via RLS). The expense
 * amount is the employer's monthly cost = gross_salary (minor units). Throws
 * NoSalarySetError when the employee has no gross salary set.
 */
export function useRecordSalaryExpense() {
  const qc = useQueryClient();
  const { claims } = useAuth();
  return useMutation({
    mutationFn: async (staff: StaffRow) => {
      if (staff.gross_salary == null) throw new NoSalarySetError();
      const now = new Date();
      const month = now.toISOString().slice(0, 7); // YYYY-MM
      const today = now.toISOString().slice(0, 10); // YYYY-MM-DD
      const { error } = await supabase.from('expenses').insert({
        tenant_id: claims?.tenant_id ?? '',
        category: 'salary',
        amount: staff.gross_salary,
        description: `Плата: ${staff.first_name} ${staff.last_name} (${month})`,
        incurred_on: today,
        recorded_by_staff_id: claims?.staff_id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['profit_loss'] });
    },
  });
}

export interface ShiftRow {
  id: string;
  staff_id: string;
  location_id: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  clock_in_at: string | null;
  clock_out_at: string | null;
}

export function useStaffShifts(range: { from: string; to: string; staffId?: string }) {
  return useQuery({
    queryKey: ['staff_shifts', range],
    queryFn: async (): Promise<ShiftRow[]> => {
      const { data, error } = await listShifts(supabase, range);
      if (error) throw error;
      return (data ?? []) as unknown as ShiftRow[];
    },
  });
}

export function useClockIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: schemas.ClockInInput) => clockIn(supabase, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff_shifts'] }),
  });
}

export function useClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: schemas.ClockOutInput) => clockOut(supabase, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff_shifts'] }),
  });
}

export interface CommissionRuleRow {
  id: string;
  staff_id: string | null;
  app_role: string | null;
  basis: string;
  rate_bps: number | null;
  flat_amount: number | null;
  is_active: boolean;
}

export function useCommissionRules() {
  return useQuery({
    queryKey: ['commission_rules'],
    queryFn: async (): Promise<CommissionRuleRow[]> => {
      const { data, error } = await listCommissionRules(supabase, { activeOnly: true });
      if (error) throw error;
      return (data ?? []) as unknown as CommissionRuleRow[];
    },
  });
}

export function useCreateCommissionRule() {
  const qc = useQueryClient();
  const { claims } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateCommissionRuleInput) => {
      const { data, error } = await createCommissionRule(supabase, claims?.tenant_id ?? '', input);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['commission_rules'] }),
  });
}

export function useDeactivateCommissionRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await deactivateCommissionRule(supabase, id);
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['commission_rules'] }),
  });
}

export interface CommissionSummaryRow {
  staff_id: string;
  staff_name: string;
  app_role: string;
  basis: string;
  transaction_count: number;
  total_amount: number;
  fully_paid_out: boolean;
}

export function useCommissionSummary(range: { from?: string; to?: string; staffId?: string }) {
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  return useQuery({
    queryKey: ['commission_summary', tenantId, range],
    queryFn: async (): Promise<CommissionSummaryRow[]> => {
      const { data, error } = await listCommissionSummary(supabase, { ...range, tenantId });
      if (error) throw error;
      return (data ?? []) as unknown as CommissionSummaryRow[];
    },
  });
}
